import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  useLocation,
  generatePath,
} from 'react-router-dom';
import copy from 'copy-to-clipboard';
import {
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  SidePanelLoader,
  Popup,
  IconCopy,
  PermissionEnum,
  getResponseErrors,
  ResponseError,
  Tabs,
  PermissionDenied,
  SecurityLockForm,
  SecuritySidepanelLocksList,
  SecuritySidepanelLogsList,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearLockPanelState, clearPanelState } from './store/slice';
import {
  useCreateClientLockMutation,
  useLazyGetClientAuditLogsQuery,
  useLazyGetClientLockItemAuditLogsQuery,
  useLazyGetClientLockItemQuery,
  useLazyGetClientLocksQuery,
  useLazyGetClientQuery,
  useResetClientPasswordMutation,
  useUpdateClientLockMutation,
  useLazyGetClientActiveLocksQuery,
  useUpdateClientMutation,
} from './store/service';
import ClientDetails from '../../components/clients/details';
import {
  schema as logsSchema,
  columns,
  columnsLabels,
  filtersSchema,
} from './variables/lock-item-logs';
import {
  schema as locksSchema,
  columns as locksColumns,
  columnsLabels as locksColumnsLabels,
  filtersSchema as locksFiltersSchema,
} from './variables/locks';
import {
  schema as auditLogsSchema,
  columns as auditLogsColumns,
  columnsLabels as auditLogsColumnsLabels,
  filtersSchema as auditLogsFiltersSchema,
} from './variables/logs';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const Client = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [popupUpVisible, setPopupVisible] = useState(false);
  const [token, setToken] = useState('');
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [errorList, setErrorList] = useState('');
  const [activeTab, setActiveTab] = useState<'general' | 'locks' | 'logs'>(
    'general',
  );
  const [lockId, setLockId] = useState('');
  const [lockFormVisible, setLockFormVisible] = useState(false);
  const [activeStatus, setActiveStatus] = useState('');

  const {
    clients: {
      client,
      lockItem,
      lockItemAuditLogs,
      lockItemAuditLogsPagination,
      locks,
      locksPagination,
      auditLogs,
      auditLogsPagination,
      activeLock,
    },
    userList: {
      users: { users: employees },
    },
    administrationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { clientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const [getClient, { isLoading, isFetching }] = useLazyGetClientQuery();
  const [resetCustomerPassword, { isLoading: recoveryInProgress }] =
    useResetClientPasswordMutation();
  const [
    getLockItem,
    { isLoading: lockItemIsLoading, isFetching: lockItemIsFetching },
  ] = useLazyGetClientLockItemQuery();
  const [getLocks, { isLoading: locksIsLoading, isFetching: locksIsFetching }] =
    useLazyGetClientLocksQuery();
  const [create, { isLoading: requestInProgress, isSuccess }] =
    useCreateClientLockMutation();
  const [
    update,
    { isLoading: updateInProgress, isSuccess: updateLockIsSuccess },
  ] = useUpdateClientLockMutation();
  const [updateClient, { isLoading: updateClientInProgress }] =
    useUpdateClientMutation();
  const [
    getLockItemLogsList,
    { isFetching: lockItemLogsIsFetching, isLoading: lockItemLogsIsLoading },
  ] = useLazyGetClientLockItemAuditLogsQuery();
  const [
    getListAuditLogs,
    { isFetching: auditLogsIsFetching, isLoading: auditLogsIsLoading },
  ] = useLazyGetClientAuditLogsQuery();
  const [getClientActiveLock] = useLazyGetClientActiveLocksQuery();

  const handleClose = () => {
    setErrorList('');
    setPanelIsOpen(false);
    dispatch(clearPanelState());

    setTimeout(() => {
      navigate(`${ROUTES.CLIENTS_LIST}${search || ''}`);
    }, 100);
  };

  const onResetPassword = async () => {
    const response = (await resetCustomerPassword(clientId as string)) as {
      data: { data: { token: string } };
    };

    if (response && response.data && response.data.data) {
      setToken(response.data.data.token);
      setPopupVisible(true);
    }
  };

  const onUpdateClientStatus = (status: 'ACTIVE' | 'DISABLED') => {
    setActiveStatus(status);

    updateClient({ id: clientId as string, body: { status } });
  };

  const getClientAction = useCallback(async (id: string) => {
    const response = (await getClient(id)) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({ error: response?.error, setErrorList });
    }
  }, []);

  useEffect(() => {
    if (clientId) {
      setPanelIsOpen(true);
      getClientAction(clientId);
      getClientActiveLock(clientId);
    }
  }, [clientId]);

  useEffect(() => {
    if (client) {
      setActiveStatus(client?.status);
    }
  }, [client]);

  useEffect(() => {
    if (isSuccess || updateLockIsSuccess) {
      getClientActiveLock(clientId as string);
    }
  }, [isSuccess, updateLockIsSuccess]);

  let link: string;

  if (process.env.CUSTOMERS_HOST) {
    link = process.env.CUSTOMERS_HOST;
  } else {
    link = window.location.host.includes('baseella')
      ? window.location.host.replace('backoffice', 'customers')
      : '';
  }

  const onCopy = () => {
    copy(`${link}/auth/reset-password?token=${token}`);

    setCopyIsSuccess(true);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <Tabs
          tabs={[
            { name: 'Details', value: 'general' },
            { name: 'Locks', value: 'locks' },
            { name: 'Audit Logs', value: 'logs' },
          ]}
          activeTab={activeTab}
          wrapperClassName={cn.clientTabs}
          onClick={(tab) => {
            setActiveTab(tab as 'general' | 'locks' | 'logs');
          }}
        />
        {activeTab === 'general' && (
          <ClientDetails
            onResetPassword={onResetPassword}
            recoveryInProgress={recoveryInProgress}
            clientId={clientId}
            changeLogsAvailable={activePermissions?.includes(
              PermissionEnum.AUDIT_LOG_LIST,
            )}
            activeStatus={activeStatus as 'ACTIVE' | 'DISABLED'}
            onUpdateStatus={onUpdateClientStatus}
            updateClientInProgress={updateClientInProgress}
            client={client}
            activeLock={activeLock}
            resetPasswordAvailable={activePermissions?.includes(
              PermissionEnum.USERS_EDIT_ACTION,
            )}
            onViewLocksClick={() => {
              setActiveTab('locks');

              navigate(
                `${generatePath(ROUTES.CLIENTS_EDIT, {
                  clientId: clientId as string,
                })}?locked=true`,
              );

              if (locks) {
                getLocks(`?take=20&userId=${clientId}&locked=true`);
              }
            }}
          />
        )}
        {activeTab === 'logs' && (
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <SecuritySidepanelLogsList
                auditLogs={auditLogs}
                auditLogsPagination={auditLogsPagination}
                panelIsOpen={panelIsOpen}
                activePermissions={activePermissions}
                userId={clientId}
                getList={getListAuditLogs}
                schema={auditLogsSchema}
                filtersSchema={auditLogsFiltersSchema}
                isFetching={auditLogsIsFetching}
                isLoading={auditLogsIsLoading}
                columns={auditLogsColumns}
                columnsLabels={auditLogsColumnsLabels}
                listClassName={cn.securityLocksContent}
                withoutChangeSearchParams
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        )}
        {activeTab === 'locks' && (
          <>
            {activePermissions?.includes(PermissionEnum.LOCKS_LIST) && (
              <SecuritySidepanelLocksList
                users={employees}
                locks={locks}
                locksPagination={locksPagination}
                userId={clientId}
                getList={getLocks}
                schema={locksSchema}
                filtersSchema={locksFiltersSchema}
                setLockId={setLockId}
                onBlockUserClick={() => {
                  setPanelIsOpen(false);
                  setLockFormVisible(true);
                }}
                isFetching={locksIsFetching}
                activePermissions={activePermissions}
                panelIsOpen={panelIsOpen}
                isLoading={locksIsLoading}
                columns={locksColumns}
                columnsLabels={locksColumnsLabels}
                listClassName={cn.securityLocksContent}
                withoutChangeSearchParams
              />
            )}
            {!activePermissions?.includes(PermissionEnum.LOCKS_LIST) && (
              <PermissionDenied />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <SecurityLockForm
        getLockItem={getLockItem}
        getLocks={getLocks}
        create={create}
        update={update}
        lockItem={lockItem}
        takeCount={20}
        activePermissions={activePermissions}
        employees={employees}
        clearLockPanelState={() => dispatch(clearLockPanelState())}
        onClose={() => {
          setLockFormVisible(false);

          setPanelIsOpen(true);
          setLockId('');
        }}
        onBlockUserClick={() => {
          setPanelIsOpen(false);
          setLockFormVisible(true);
        }}
        isSuccess={isSuccess}
        isLoading={lockItemIsLoading}
        isFetching={lockItemIsFetching}
        userId={clientId}
        requestInProgress={requestInProgress}
        updateInProgress={updateInProgress}
        lockIdValue={lockId}
        formPanelVisible={lockFormVisible}
        lockItemAuditLogsPagination={lockItemAuditLogsPagination}
        lockItemAuditLogs={lockItemAuditLogs}
        getLockItemLogsList={getLockItemLogsList}
        lockItemLogsSchema={logsSchema}
        lockItemLogsFiltersSchema={filtersSchema}
        lockItemLogsColumns={columns}
        lockItemLogsColumnsLabels={columnsLabels}
        lockItemLogsIsLoading={lockItemLogsIsLoading}
        lockItemLogsIsFetching={lockItemLogsIsFetching}
        withoutUserSelector
      />
      <SidePanel
        title="Client details"
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={handleClose}
        footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
        closeOnClickOutside
        sidepanelExpandedKey="client-details"
        errorSidepanel={errorList}
      >
        {content}
      </SidePanel>
      <Popup
        title="Reset customer password"
        description="Send this link to the user for password recovery"
        onCancelClick={() => setPopupVisible(false)}
        isVisible={popupUpVisible}
        parent={document.getElementById('password-popup') as HTMLElement}
      >
        <div className={cn.resetPasswordLink}>
          <div
            className={`${cn.copySuccess} ${copyIsSuccess ? cn.visible : ''}`}
          >
            Copied!
          </div>
          <span>{`${link}/auth/reset-password?token=${token}`}</span>
          <button
            type="button"
            className={cn.copyButton}
            onClick={onCopy}
            aria-label="Copy link button"
          >
            <IconCopy />
          </button>
        </div>
      </Popup>
      <div id="password-popup" />
    </>
  );
};

export default Client;
